import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listCountries, PRIORITY_COUNTRIES, type Country } from '../../lib/countries';
import { Flag } from './Flag';
import { Check, ChevronDown, Close, Search } from './icons';

/**
 * Searchable country picker: a 48px trigger showing flag + dial code. On narrow screens it
 * opens as a full-height page (route-less overlay sized to the visual viewport): the search
 * field at the top, the list filling the rest and scrolling under the keyboard, priority
 * countries first, the detected country highlighted. Desktop keeps the popover. Search matches
 * name, ISO code and dial code.
 */
export function CountrySelect({
  value,
  onChange,
  disabled,
  detected,
}: {
  value: CountryCode;
  onChange: (code: CountryCode) => void;
  disabled?: boolean;
  /** Country detected for this person (server hint or browser), highlighted in the list. */
  detected?: CountryCode | undefined;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const countries = useMemo(() => listCountries(locale), [locale]);
  const byCode = useMemo(() => new Map(countries.map((c) => [c.code, c])), [countries]);
  const current = byCode.get(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) {
      const first = [
        ...new Set([detected, value, ...PRIORITY_COUNTRIES].filter(Boolean)),
      ] as CountryCode[];
      const priority = first.map((c) => byCode.get(c)).filter((c): c is Country => Boolean(c));
      const rest = countries.filter((c) => !first.includes(c.code));
      return [...priority, ...rest];
    }
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase() === q ||
        c.dialCode.replace('+', '').startsWith(q),
    );
  }, [countries, byCode, query, detected, value]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const select = (code: CountryCode) => {
    onChange(code);
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('signin.country')}: ${current?.name ?? value} ${current?.dialCode ?? ''}`}
        className={[
          'control flex h-12 shrink-0 items-center gap-2 rounded-md border border-line-strong bg-surface px-3',
          'text-base text-ink hover:bg-surface-2 disabled:opacity-70',
        ].join(' ')}
        dir="ltr"
      >
        <Flag code={value} />
        <span className="tabular font-medium">{current?.dialCode}</span>
        <ChevronDown size={16} className="text-ink-3" />
      </button>

      {open ? (
        <div
          className="vv-root z-50 bg-surface sm:top-0 sm:h-auto sm:min-h-dvh sm:items-center sm:justify-center sm:bg-ink/40 sm:p-6 sm:backdrop-blur-[2px]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('signin.country')}
            className="flex min-h-0 flex-1 flex-col bg-surface sm:max-h-[70vh] sm:w-full sm:max-w-md sm:flex-none sm:rounded-xl sm:shadow-lg"
          >
            <div className="flex shrink-0 items-center gap-2 border-b border-line p-3 pt-safe sm:pt-3">
              <div className="control flex h-12 flex-1 items-center gap-2 rounded-md border border-line-strong bg-surface px-3">
                <Search size={18} className="text-ink-3" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('signin.searchCountry')}
                  aria-label={t('signin.searchCountry')}
                  aria-controls={listId}
                  enterKeyHint="search"
                  className="h-full min-w-0 flex-1 bg-transparent text-base"
                  autoComplete="off"
                />
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common.close')}
                className="flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-surface-2"
              >
                <Close size={20} />
              </button>
            </div>
            <ul
              id={listId}
              role="listbox"
              aria-activedescendant={undefined}
              className="scroller min-h-0 flex-1 p-1 pb-safe"
            >
              {filtered.length === 0 ? (
                <li className="p-4 text-center text-sm text-ink-2">{t('signin.noCountry')}</li>
              ) : null}
              {filtered.map((c) => {
                const selected = c.code === value;
                const isDetected = c.code === detected;
                return (
                  <li key={c.code} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      onClick={() => select(c.code)}
                      className={[
                        'flex min-h-12 w-full items-center gap-3 rounded-sm px-3 text-start',
                        selected ? 'bg-brand-soft text-ink' : 'hover:bg-surface-2',
                      ].join(' ')}
                    >
                      <Flag code={c.code} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-base">{c.name}</span>
                        {isDetected ? (
                          <span className="text-xs text-ink-3">{t('signin.detectedCountry')}</span>
                        ) : null}
                      </span>
                      <span className="tabular text-sm text-ink-2" dir="ltr">
                        {c.dialCode}
                      </span>
                      {selected ? <Check size={18} className="text-brand" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
}
